import axios from "axios"
import {
  ARBITRUM_RPC_URL,
  ART_BLOCKS_METADATA_GRAPHQL_URL,
  BASE_RPC_URL,
  ETHEREUM_RPC_URL,
  FeaturedProject,
} from "config"
import { OrderDirection } from "utils/types"
import { getArtBlocksTokenUrl } from "utils/artBlocksUrls"
import { NftTokenMetadata } from "utils/tokenMetadata"
import {
  buildFeatureFacets,
  FeatureFacet,
  FeatureFilters,
  filtersToFeatureJson,
  hasFeatureFilters,
} from "utils/featureFilters"

export interface ArtBlocksProjectMetadataToken {
  id: string
  token_id: string
  invocation: number
  owner_address?: string
  minted_at?: string
  media_url?: string
  live_view_url?: string
  features?: Record<string, unknown>
  hash?: string
  mint_transaction_hash?: string
}

export interface ArtBlocksProjectMetadata {
  id: string
  name: string
  description?: string
  license?: string
  invocations: number
  max_invocations: number
  aspect_ratio?: number
  contract_address: string
  chain_id: number
  project_id: string
  artist_name: string
  artist_address: string
  complete?: boolean
  active?: boolean
  tokens?: ArtBlocksProjectMetadataToken[]
  tokens_aggregate?: {
    aggregate?: {
      count?: number
    }
  }
}

export interface ArtBlocksTokenMetadata extends ArtBlocksProjectMetadataToken {
  contract_address: string
  chain_id: number
  project_id: string
  project?: {
    id: string
    name: string
    artist_name: string
    aspect_ratio?: number
    description?: string
    license?: string
    invocations?: number
    max_invocations?: number
  }
}

export interface ArtBlocksWalletMetadata {
  display_name?: string | null
  public_address: string
}

export interface ArtBlocksTransferMetadata {
  id: string
  chain_id: number
  token_id: string
  from_address: string
  to_address: string
  transaction_hash: string
  block_number: number | string
  block_timestamp: number
  from_wallet?: ArtBlocksWalletMetadata | null
  to_wallet?: ArtBlocksWalletMetadata | null
  native_value?: string
}

export interface ArtBlocksTokenProvenanceMetadata {
  id: string
  token_id: string
  project_name?: string | null
  owner_address: string
  owner?: ArtBlocksWalletMetadata | null
  mint_transaction_hash?: string | null
  minted_at?: string
  last_transferred_at?: string | null
  list_price?: number | null
  list_eth_price?: number | null
  list_currency_symbol?: string | null
  list_url?: string | null
  primary_purchase_details?: {
    transaction_hash: string
    currency_symbol?: string | null
    currency_address?: string | null
    minter_address?: string | null
  } | null
  transfers: ArtBlocksTransferMetadata[]
}

export interface ArtBlocksProjectFeatureFacetMetadata {
  facets: FeatureFacet[]
  totalCount: number
  loadedCount: number
  truncated: boolean
}

interface GraphqlResponse<T> {
  data?: T
  errors?: { message: string }[]
}

interface JsonRpcResponse<T> {
  id: number
  result?: T
  error?: {
    code: number
    message: string
  }
}

interface JsonRpcTransaction {
  hash: string
  value: string
}

interface ArtBlocksProjectTokenQueryParams {
  contractAddress: string
  projectId: string
  chainId: number
  limit: number
  offset: number
  orderDirection: OrderDirection
  featureFilters?: FeatureFilters
}

interface ArtBlocksProjectFeatureFacetQueryParams {
  contractAddress: string
  projectId: string
  chainId: number
}

const FEATURE_FACET_BATCH_SIZE = 1000
const FEATURE_FACET_MAX_TOKENS = 20000

const requestArtBlocksMetadata = async <T>(query: string, variables: Record<string, unknown>) => {
  const response = await axios.post<GraphqlResponse<T>>(ART_BLOCKS_METADATA_GRAPHQL_URL, {
    query,
    variables,
  })

  if (response.data.errors?.length) {
    throw new Error(response.data.errors.map((error) => error.message).join(", "))
  }

  return response.data.data as T
}

const getRpcUrlForChain = (chainId: number) => {
  if (chainId === 1) return ETHEREUM_RPC_URL
  if (chainId === 42161) return ARBITRUM_RPC_URL
  if (chainId === 8453) return BASE_RPC_URL

  return undefined
}

const fetchNativeTransactionValues = async (chainId: number, transactionHashes: string[]) => {
  const rpcUrl = getRpcUrlForChain(chainId)
  const uniqueHashes = Array.from(new Set(transactionHashes.map((hash) => hash.toLowerCase()).filter(Boolean)))

  if (!rpcUrl || uniqueHashes.length === 0) return {}

  const response = await axios.post<JsonRpcResponse<JsonRpcTransaction | null>[]>(
    rpcUrl,
    uniqueHashes.map((hash, index) => ({
      jsonrpc: "2.0",
      id: index,
      method: "eth_getTransactionByHash",
      params: [hash],
    }))
  )

  return response.data.reduce<Record<string, string>>((values, item) => {
    const hash = uniqueHashes[item.id]
    if (hash && item.result?.value) values[hash] = item.result.value

    return values
  }, {})
}

export const getFeaturedProjectTokenIdForInvocation = (project: FeaturedProject, invocation: number) => {
  const projectId = Number(project.projectId)
  if (!Number.isFinite(projectId) || projectId === 0) return invocation.toString()
  return (projectId * 1000000 + invocation).toString()
}

export const fetchFeaturedProjectMetadata = async (project: FeaturedProject) => {
  const query = `
    query FeaturedProject($contract: String!, $projectId: String!, $chainId: Int!) {
      projects_metadata(
        where: {
          contract_address: { _eq: $contract }
          project_id: { _eq: $projectId }
          chain_id: { _eq: $chainId }
        }
        limit: 1
      ) {
        id
        name
        description
        license
        invocations
        max_invocations
        aspect_ratio
        contract_address
        chain_id
        project_id
        artist_name
        artist_address
        complete
        active
        tokens_aggregate {
          aggregate {
            count
          }
        }
      }
    }
  `

  const data = await requestArtBlocksMetadata<{ projects_metadata: ArtBlocksProjectMetadata[] }>(query, {
    contract: project.contractAddress.toLowerCase(),
    projectId: project.projectId,
    chainId: project.chainId,
  })

  return data.projects_metadata[0]
}

export const fetchFeaturedProjectTokens = async (
  project: FeaturedProject,
  limit: number,
  offset: number,
  orderDirection: OrderDirection,
  featureFilters?: FeatureFilters
) => {
  return fetchArtBlocksProjectTokens({
    contractAddress: project.contractAddress,
    projectId: project.projectId,
    chainId: project.chainId,
    limit,
    offset,
    orderDirection,
    featureFilters,
  })
}

export const fetchArtBlocksProjectTokens = async ({
  contractAddress,
  projectId,
  chainId,
  limit,
  offset,
  orderDirection,
  featureFilters,
}: ArtBlocksProjectTokenQueryParams) => {
  const order = orderDirection === OrderDirection.ASC ? "asc" : "desc"
  const hasFilters = hasFeatureFilters(featureFilters)
  const featureFilterVariable = hasFilters ? ", $features: jsonb!" : ""
  const tokenWhereArgument = hasFilters ? ", where: { features: { _contains: $features } }" : ""
  const aggregateWhereArgument = hasFilters ? "(where: { features: { _contains: $features } })" : ""
  const query = `
    query ProjectTokens($contract: String!, $projectId: String!, $chainId: Int!, $limit: Int!, $offset: Int!${featureFilterVariable}) {
      projects_metadata(
        where: {
          contract_address: { _eq: $contract }
          project_id: { _eq: $projectId }
          chain_id: { _eq: $chainId }
        }
        limit: 1
      ) {
        id
        name
        invocations
        max_invocations
        aspect_ratio
        contract_address
        chain_id
        project_id
        artist_name
        artist_address
        license
        complete
        active
        tokens(limit: $limit, offset: $offset, order_by: { invocation: ${order} }${tokenWhereArgument}) {
          id
          token_id
          invocation
          owner_address
          minted_at
          media_url
          live_view_url
          features
          hash
          mint_transaction_hash
        }
        tokens_aggregate${aggregateWhereArgument} {
          aggregate {
            count
          }
        }
      }
    }
  `

  const data = await requestArtBlocksMetadata<{ projects_metadata: ArtBlocksProjectMetadata[] }>(query, {
    contract: contractAddress.toLowerCase(),
    projectId,
    chainId,
    limit,
    offset,
    ...(hasFilters ? { features: filtersToFeatureJson(featureFilters || {}) } : {}),
  })

  return data.projects_metadata[0]
}

export const fetchArtBlocksProjectFeatureFacets = async ({
  contractAddress,
  projectId,
  chainId,
}: ArtBlocksProjectFeatureFacetQueryParams): Promise<ArtBlocksProjectFeatureFacetMetadata> => {
  const query = `
    query ProjectFeatureFacets($contract: String!, $projectId: String!, $chainId: Int!, $limit: Int!, $offset: Int!) {
      projects_metadata(
        where: {
          contract_address: { _eq: $contract }
          project_id: { _eq: $projectId }
          chain_id: { _eq: $chainId }
        }
        limit: 1
      ) {
        tokens(limit: $limit, offset: $offset, order_by: { invocation: asc }) {
          id
          features
        }
        tokens_aggregate {
          aggregate {
            count
          }
        }
      }
    }
  `

  const tokens: ArtBlocksProjectMetadataToken[] = []
  let totalCount = 0
  let offset = 0

  while (offset < FEATURE_FACET_MAX_TOKENS) {
    const data = await requestArtBlocksMetadata<{ projects_metadata: ArtBlocksProjectMetadata[] }>(query, {
      contract: contractAddress.toLowerCase(),
      projectId,
      chainId,
      limit: FEATURE_FACET_BATCH_SIZE,
      offset,
    })
    const projectMetadata = data.projects_metadata[0]
    const batch = projectMetadata?.tokens || []
    totalCount = projectMetadata?.tokens_aggregate?.aggregate?.count || totalCount
    tokens.push(...batch)

    if (!projectMetadata || batch.length < FEATURE_FACET_BATCH_SIZE || tokens.length >= totalCount) break
    offset += FEATURE_FACET_BATCH_SIZE
  }

  return {
    facets: buildFeatureFacets(tokens),
    totalCount,
    loadedCount: tokens.length,
    truncated: totalCount > tokens.length,
  }
}

export const fetchArtBlocksTokenMetadata = async (project: FeaturedProject, tokenId: string) => {
  const query = `
    query FeaturedToken($id: String!, $chainId: Int!) {
      tokens_metadata_by_pk(id: $id, chain_id: $chainId) {
        id
        token_id
        invocation
        owner_address
        minted_at
        media_url
        live_view_url
        features
        hash
        mint_transaction_hash
        contract_address
        chain_id
        project_id
        project {
          id
          name
          artist_name
          aspect_ratio
          description
          license
          invocations
          max_invocations
        }
      }
    }
  `

  const data = await requestArtBlocksMetadata<{ tokens_metadata_by_pk: ArtBlocksTokenMetadata | null }>(query, {
    id: `${project.contractAddress.toLowerCase()}-${tokenId}`,
    chainId: project.chainId,
  })

  return data.tokens_metadata_by_pk
}

export const fetchArtBlocksTokenProvenance = async (
  contractAddress: string,
  tokenId: string,
  chainId: number
) => {
  const query = `
    query TokenProvenance($id: String!, $chainId: Int!) {
      tokens_metadata_by_pk(id: $id, chain_id: $chainId) {
        id
        token_id
        project_name
        owner_address
        owner {
          display_name
          public_address
        }
        mint_transaction_hash
        minted_at
        last_transferred_at
        list_price
        list_eth_price
        list_currency_symbol
        list_url
        primary_purchase_details {
          transaction_hash
          currency_symbol
          currency_address
          minter_address
        }
        transfers(order_by: { block_timestamp: asc }, limit: 100) {
          id
          chain_id
          token_id
          from_address
          to_address
          transaction_hash
          block_number
          block_timestamp
          from_wallet {
            display_name
            public_address
          }
          to_wallet {
            display_name
            public_address
          }
        }
      }
    }
  `

  const data = await requestArtBlocksMetadata<{
    tokens_metadata_by_pk: ArtBlocksTokenProvenanceMetadata | null
  }>(query, {
    id: `${contractAddress.toLowerCase()}-${tokenId}`,
    chainId,
  })
  const token = data.tokens_metadata_by_pk

  if (!token) return null

  let nativeValues: Record<string, string> = {}
  try {
    nativeValues = await fetchNativeTransactionValues(
      chainId,
      token.transfers.map((transfer) => transfer.transaction_hash)
    )
  } catch {
    nativeValues = {}
  }

  return {
    ...token,
    transfers: token.transfers.map((transfer) => ({
      ...transfer,
      native_value: nativeValues[transfer.transaction_hash.toLowerCase()],
    })),
  }
}

export const fetchNftTokenMetadata = async (project: FeaturedProject, tokenId: string) => {
  const url = getArtBlocksTokenUrl({
    chainId: project.chainId,
    contractAddress: project.contractAddress,
    tokenId,
  })
  const response = await axios.get<NftTokenMetadata>(url)
  return response.data
}
